import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
    X, Save, AlertCircle, Loader2, Dumbbell, 
    Plus, Search, Filter, Timer, Trash2, Eye,
    ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
    Grid, List, Target, Zap, Clock, Weight, GripVertical,
    Maximize2, Minimize2, XCircle, Image, Edit
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import NewExerciseSelector from '../common/NewExerciseSelector';
import MuscleBadges from '../common/MuscleBadges';
import ImprovedUserExerciseForm from '../Standalone/ImprovedUserExerciseForm';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import usePaginatedExercises from '../../hooks/usePaginatedExercises';
import RecoveryMinSecInputs from './common/RecoveryMinSecInputs';
// Componente locale per esercizio ordinabile
const SortableExerciseItem = ({ exercise, previousExercise, onUpdate, onRemove, onToggleDetails, isExpanded, isFirst, t }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: exercise.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`rounded-lg border border-[#dad8e5] bg-white p-4 dark:border-white/10 dark:bg-gray-900 ${
                isDragging ? 'shadow-lg' : 'shadow-sm'
            } transition-all duration-200`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div
                        {...attributes}
                        {...listeners}
                        className="cursor-grab p-1 text-gray-400 hover:cursor-grabbing hover:text-[#372d83] dark:hover:text-white/75"
                    >
                        <GripVertical className="h-4 w-4" />
                    </div>
                    
                    {/* Immagine esercizio */}
                    <div className="w-12 h-12 flex-shrink-0">
                        {exercise.immagine_nome ? (
                            <img
                                src={`/api/serve_image.php?filename=${encodeURIComponent(exercise.immagine_nome)}`}
                                alt={exercise.nome}
                                className="h-full w-full select-none rounded-lg bg-[#eeebf5] object-cover dark:bg-gray-800"
                                onContextMenu={(e) => e.preventDefault()}
                                draggable={false}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div 
                            className="hidden h-full w-full flex items-center justify-center rounded-lg bg-[#eeebf5] dark:bg-gray-800"
                        >
                            <Dumbbell className="h-6 w-6 text-[#bca8d1] dark:text-white/45" />
                        </div>
                    </div>
                    
                    <div className="flex-1">
                        <h3 className="font-medium text-[#3a3a3a] dark:text-white">{exercise.nome}</h3>
                        {exercise.descrizione && (
                            <p className="mt-1 text-sm text-[#3a3a3a]/80 dark:text-white/70">{exercise.descrizione}</p>
                        )}
                        
                        {/* Badge muscoli */}
                        <div className="mt-2">
                            <MuscleBadges
                                primaryMuscle={exercise.primary_muscle}
                                secondaryMuscles={exercise.secondary_muscles}
                                variant="compact"
                                showCategory={false}
                            />
                        </div>
                        
                        {/* Badge tipo di serie */}
                        <div className="mt-2 flex items-center space-x-2">
                            {exercise.set_type && exercise.set_type !== 'normal' && (
                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                    exercise.set_type === 'superset' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' :
                                    exercise.set_type === 'dropset' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                                    exercise.set_type === 'circuit' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' :
                                    exercise.set_type === 'giant_set' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' :
                                    'bg-[#eeebf5] text-[#3a3a3a] dark:bg-gray-800 dark:text-white/90'
                                }`}>
                                    {exercise.set_type === 'superset' ? 'Superset' :
                                     exercise.set_type === 'dropset' ? 'Dropset' :
                                     exercise.set_type === 'circuit' ? 'Circuit' :
                                     exercise.set_type === 'giant_set' ? 'Giant Set' :
                                     exercise.set_type}
                                </span>
                            )}
                            {exercise.linked_to_previous && (
                                <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 font-medium">
                                    🔗 Collegato
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center space-x-2">
                    <button
                        type="button"
                        onClick={() => onToggleDetails(exercise.id)}
                        className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-[#3a3a3a]/80 transition-colors hover:bg-[#e3deef] hover:text-[#372d83] dark:text-white/70 dark:hover:bg-white/10 dark:hover:text-gray-300"
                    >
                        {isExpanded ? (
                            <>
                                <ChevronUp className="h-3.5 w-3.5" />
                                <span>{t('gym.membersManager.workoutModal.hideParameters')}</span>
                            </>
                        ) : (
                            <>
                                <ChevronDown className="h-3.5 w-3.5" />
                                <span>{t('gym.membersManager.workoutModal.configureParameters')}</span>
                            </>
                        )}
                    </button>
                    
                    <button
                        type="button"
                        onClick={() => onRemove(exercise.id)}
                        className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Dettagli espandibili */}
            {isExpanded && (
                <div className="mt-4 border-t border-[#dad8e5] pt-4 dark:border-white/10">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                Serie
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={exercise.serie ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    onUpdate(exercise.id, 'serie', value === '' ? '' : parseInt(value) || '');
                                }}
                                onBlur={(e) => {
                                    // Applica default solo quando perde il focus
                                    if (e.target.value === '' || e.target.value === '0') {
                                        onUpdate(exercise.id, 'serie', 3);
                                    }
                                }}
                                onFocus={(e) => e.target.select()}
                                className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                            />
                        </div>
                        
                        <div>
                            <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                {(() => {
                                    const isCardio = exercise.is_cardio === 1;
                                    const isIsometricScheda = exercise.is_isometric_scheda === 1;
                                    if (isCardio) return 'Minuti';
                                    if (isIsometricScheda) return 'Secondi';
                                    return 'Ripetizioni';
                                })()}
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={exercise.ripetizioni ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    onUpdate(exercise.id, 'ripetizioni', value === '' ? '' : parseInt(value) || '');
                                }}
                                onBlur={(e) => {
                                    // Applica default solo quando perde il focus
                                    if (e.target.value === '' || e.target.value === '0') {
                                        onUpdate(exercise.id, 'ripetizioni', 12);
                                    }
                                }}
                                onFocus={(e) => e.target.select()}
                                className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                                placeholder={(() => {
                                    const isCardio = exercise.is_cardio === 1;
                                    const isIsometricScheda = exercise.is_isometric_scheda === 1;
                                    if (isCardio) return 'Minuti (es. 30)';
                                    if (isIsometricScheda) return 'Secondi di tenuta';
                                    return 'Numero ripetizioni';
                                })()}
                            />
                        </div>
                        
                        {/* Peso - nascosto per esercizi cardio */}
                        {exercise.is_cardio !== 1 && (
                        <div>
                            <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                Peso (kg)
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={exercise.peso ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    onUpdate(exercise.id, 'peso', value === '' ? '' : parseFloat(value) || '');
                                }}
                                onBlur={(e) => {
                                    // Applica default 0 solo quando perde il focus
                                    if (e.target.value === '') {
                                        onUpdate(exercise.id, 'peso', 0);
                                    }
                                }}
                                onFocus={(e) => e.target.select()}
                                className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                            />
                        </div>
                        )}
                        
                        <RecoveryMinSecInputs
                            valueSeconds={exercise.tempo_recupero}
                            onSecondsChange={(sec) => onUpdate(exercise.id, 'tempo_recupero', sec)}
                            defaultTotalSeconds={90}
                            focusColor="emerald"
                            gymDarkInputs
                        />
                    </div>
                    
                    {/* Tipo di serie */}
                    <div className="mt-4">
                        <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                            Tipo di Serie
                        </label>
                        <select
                            value={exercise.set_type || 'normal'}
                            onChange={(e) => onUpdate(exercise.id, 'set_type', e.target.value)}
                            className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                        >
                            <option value="normal">Normale</option>
                            <option value="superset">Superset</option>
                            <option value="dropset">Dropset</option>
                            <option value="circuit">Circuit</option>
                            <option value="giant_set">Giant Set</option>
                        </select>
                    </div>
                    
                    {/* Collegamento all'esercizio precedente */}
                    {!isFirst && exercise.set_type && exercise.set_type !== 'normal' && previousExercise && exercise.set_type === previousExercise.set_type && (
                        <div className="mt-4">
                            <div className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    id={`linked-${exercise.id}`}
                                    checked={exercise.linked_to_previous || false}
                                    onChange={(e) => onUpdate(exercise.id, 'linked_to_previous', e.target.checked)}
                                    className="h-4 w-4 rounded border-[#dad8e5] text-[#372d83] focus:ring-[#372d83]/40 dark:border-gray-700 dark:text-gray-300"
                                />
                                <label htmlFor={`linked-${exercise.id}`} className="text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                    Collegato all'esercizio precedente
                                </label>
                            </div>
                            <p className="mt-1 text-xs text-[#3a3a3a]/65 dark:text-white/60">
                                Collega questo esercizio al precedente per formare superset, dropset, ecc.
                            </p>
                        </div>
                    )}
                    
                    {/* Modalità Isometrica (solo se esercizio non è cardio) */}
                    {exercise.is_cardio !== 1 && (
                        <div className="mt-4">
                            <div className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    id={`is_isometric_scheda-${exercise.id}`}
                                    checked={exercise.is_isometric_scheda === 1}
                                    onChange={(e) => onUpdate(exercise.id, 'is_isometric_scheda', e.target.checked ? 1 : 0)}
                                    className="h-4 w-4 rounded border-[#dad8e5] text-[#372d83] focus:ring-[#372d83]/40 dark:border-gray-700 dark:text-gray-300"
                                />
                                <label htmlFor={`is_isometric_scheda-${exercise.id}`} className="text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                    Modalità Isometrica (usa secondi invece di ripetizioni)
                                </label>
                            </div>
                            <p className="ml-7 mt-1 text-xs text-[#3a3a3a]/65 dark:text-white/60">
                                Se attivo, questo esercizio verrà eseguito a tempo (secondi) invece che a ripetizioni
                            </p>
                        </div>
                    )}
                    
                    {/* Campi Cardio (solo se esercizio è cardio) */}
                    {exercise.is_cardio === 1 && (
                        <div className="mt-4 grid grid-cols-2 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                    Calorie (opzionale)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={exercise.calorie || ''}
                                    onChange={(e) => onUpdate(exercise.id, 'calorie', e.target.value ? parseInt(e.target.value) : null)}
                                    className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                                    placeholder="Calorie bruciate"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                    BPM (opzionale)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={exercise.bpm || ''}
                                    onChange={(e) => onUpdate(exercise.id, 'bpm', e.target.value ? parseInt(e.target.value) : null)}
                                    className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                                    placeholder="Battiti per minuto"
                                />
                            </div>
                        </div>
                    )}
                    
                    {/* Note Trainer - Sistema Note Duali */}
                    <div className="mt-4">
                        <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                            📝 Note Trainer
                        </label>
                        <textarea
                            value={exercise.notes?.trainer ?? ''}
                            onChange={(e) => {
                                // 🔥 DB-001: Mantieni le note esistenti (user, system) quando si modifica trainer
                                const existingNotes = exercise.notes || { trainer: null, user: null, system: null };
                                const newNotes = {
                                    trainer: e.target.value || null,
                                    user: existingNotes.user || null,
                                    system: existingNotes.system || null
                                };
                                onUpdate(exercise.id, 'notes', newNotes);
                            }}
                            rows={3}
                            maxLength={500}
                            className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                            placeholder="Aggiungi istruzioni per l'utente (es: 'Mantieni la schiena dritta', 'Controlla la discesa')"
                        />
                        <p className="mt-1 text-xs text-[#3a3a3a]/65 dark:text-white/60">
                            💡 Queste note saranno visibili all'utente durante l'allenamento
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default function CreateWorkoutModal({ client, isOpen, onClose, onSuccess }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    
    // State principale
    const [formData, setFormData] = useState({
        nome: '',
        descrizione: ''
    });
    
    // State esercizi
    const [selectedExercises, setSelectedExercises] = useState([]);
    const [expandedExercise, setExpandedExercise] = useState(null);
    
    // State per filtri avanzati (come in archivio esercizi)
    const [filters, setFilters] = useState({
        equipment: '',
        primaryMuscle: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    
    // WEBAPP-021: Usa hook paginato invece di caricamento completo
    const {
        exercises: availableExercises,
        loading: loadingExercises,
        initialLoading,
        hasMore,
        loadMore,
        search: searchExercises,
        refresh: refreshExercises
    } = usePaginatedExercises({
        initialLimit: 50,
        enableInfiniteScroll: true
    });
    
    // Gestisce il cambio filtri da NewExerciseSelector
    const handleFiltersChange = useCallback((newFilters) => {
        const newEquipment = newFilters.equipment || '';
        const newPrimaryMuscle = newFilters.primaryMuscle || '';
        
        setFilters({
            equipment: newEquipment,
            primaryMuscle: newPrimaryMuscle
        });
        
        // Applica i filtri tramite searchExercises solo se c'è almeno un filtro attivo o ricerca
        const hasFilters = newEquipment || newPrimaryMuscle;
        const hasSearch = searchTerm && searchTerm.trim().length >= 2;
        
        if (hasFilters || hasSearch) {
            searchExercises(hasSearch ? searchTerm : '', {
                equipment: newEquipment && newEquipment.trim() !== '' ? newEquipment.trim() : undefined,
                primaryMuscle: newPrimaryMuscle && newPrimaryMuscle.trim() !== '' ? newPrimaryMuscle.trim() : undefined
            });
        } else {
            // WEBAPP-GYM-011: reset filtri senza testo ricerca → ricarica elenco completo
            searchExercises('', {});
        }
    }, [searchExercises, searchTerm]);
    
    // Gestisce la ricerca testuale
    const handleSearchChange = useCallback((term) => {
        setSearchTerm(term);
        
        // Applica ricerca con filtri attivi solo se c'è almeno ricerca (>=2 caratteri) o filtri
        const hasSearch = term && term.trim().length >= 2;
        const hasFilters = filters.equipment || filters.primaryMuscle;
        
        if (hasSearch || hasFilters) {
            searchExercises(hasSearch ? term.trim() : '', {
                equipment: filters.equipment && filters.equipment.trim() !== '' ? filters.equipment.trim() : undefined,
                primaryMuscle: filters.primaryMuscle && filters.primaryMuscle.trim() !== '' ? filters.primaryMuscle.trim() : undefined
            });
        }
    }, [searchExercises, filters]);
    
    // State UI
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [showAddExerciseForm, setShowAddExerciseForm] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [activeTab, setActiveTab] = useState('selection'); // 'selection' | 'selected'
    const [isFullscreen, setIsFullscreen] = useState(false); // WEBAPP-GYM-004: Stato fullscreen
    
    // Drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (isOpen) {
            // WEBAPP-021: L'hook usePaginatedExercises gestisce già il caricamento iniziale
            setFormData({ nome: '', descrizione: '' });
            setSelectedExercises([]);
            setExpandedExercise(null);
            setError(null);
            setMessage('');
            setValidationErrors({});
            setActiveTab('selection');
        }
    }, [isOpen]);

    // Validazione in tempo reale quando cambiano i dati del form
    useEffect(() => {
        const errors = {};
        
        if (!formData.nome.trim()) {
            errors.nome = 'Il nome della scheda è obbligatorio';
            }
            
        if (selectedExercises.length === 0) {
            errors.exercises = 'Aggiungi almeno un esercizio';
        }
        
        setValidationErrors(errors);
    }, [formData.nome, selectedExercises.length]);

    const handleSelectExercise = (exercise) => {
        console.log('Esercizio selezionato:', exercise);
        
        if (!exercise.id) {
            console.error('Esercizio senza ID:', exercise);
            setError('Errore: esercizio senza ID valido');
            return;
        }
        
        const exists = selectedExercises.find(e => e.id === exercise.id);
        if (exists) {
            setSelectedExercises(prev => prev.filter(e => e.id !== exercise.id));
            if (expandedExercise === exercise.id) {
                setExpandedExercise(null);
            }
        } else {
            const newExercise = {
                ...exercise, 
                serie: exercise.serie_default || (exercise.is_cardio === 1 ? 1 : 3), // Cardio: sempre 1 serie
                ripetizioni: exercise.ripetizioni_default || 12, 
                peso: exercise.peso_default || 0,
                tempo_recupero: exercise.tempo_recupero || 90,
                notes: { trainer: null, user: null, system: null },
                set_type: 'normal',
                linked_to_previous: false,
                is_isometric_scheda: 0, // Default: non isometrico a livello scheda
                calorie: null, // Default: nessuna caloria
                bpm: null // Default: nessun BPM
            };
            
            console.log('Nuovo esercizio aggiunto:', newExercise);
            
            setSelectedExercises(prev => [...prev, newExercise]);
            // NON cambiare tab - rimani nella tab corrente (Selezione Esercizi)
        }
    };

    const updateExerciseDetail = (id, field, value) => {
        setSelectedExercises(prev =>
            prev.map(e => {
                if (e.id !== id) return e;
                
                // Validazione: se esercizio è cardio, non può essere isometrico nella stessa scheda
                if (field === 'is_isometric_scheda' && value === 1 && e.is_cardio === 1) {
                    setError('Un esercizio cardio non può essere isometrico nella stessa scheda');
                    return e; // Non modificare
                }
                
                // Se set_type è 'normal', linked_to_previous deve essere false
                if (field === 'set_type' && value === 'normal') {
                    return { ...e, [field]: value, linked_to_previous: false };
                }
                
                // Se linked_to_previous è true, il primo esercizio non può essere collegato
                if (field === 'linked_to_previous' && value === true) {
                    const exerciseIndex = prev.findIndex(ex => ex.id === id);
                    if (exerciseIndex === 0) {
                        return e; // Non modificare il primo esercizio
                    }
                }
                
                return { ...e, [field]: value };
            })
        );
    };

    const removeExercise = (id) => {
        setSelectedExercises(prev => prev.filter(e => e.id !== id));
        if (expandedExercise === id) {
            setExpandedExercise(null);
        }
    };

    const handleToggleExpanded = (exerciseId) => {
        setExpandedExercise(prev => prev === exerciseId ? null : exerciseId);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            setSelectedExercises(prev => {
                const oldIndex = prev.findIndex(item => item.id === active.id);
                const newIndex = prev.findIndex(item => item.id === over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    };

    const handleExerciseCreated = async (result) => {
        // result contiene {success: true, message: '...', exercise_id: 293}
        if (result.success && result.exercise_id) {
            try {
                // Recupera l'esercizio completo dal backend usando l'ID specifico
                const api = getAuthenticatedApi();
                const response = await api.get(`/esercizi.php?id=${result.exercise_id}`);
                
                if (response.data && response.data.id) {
                    const newExercise = response.data;
                    
                    // WEBAPP-021: Ricarica lista esercizi per includere il nuovo
                    refreshExercises();
                    
                    // Seleziona automaticamente l'esercizio appena creato
                    handleSelectExercise(newExercise);
                    
                    setMessage('Esercizio creato e aggiunto con successo!');
                    setMessageType('success');
                } else {
                    console.error('Esercizio creato ma non trovato:', result.exercise_id);
                    setMessage('Esercizio creato ma errore nel caricamento');
                    setMessageType('warning');
                }
            } catch (err) {
                console.error('Errore nel caricamento dell\'esercizio creato:', err);
                setMessage('Esercizio creato ma errore nel caricamento');
                setMessageType('warning');
            }
            
            setTimeout(() => {
                setMessage('');
                setMessageType('');
            }, 3000);
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.nome.trim()) {
            errors.nome = 'Il nome della scheda è obbligatorio';
        }
        
        if (selectedExercises.length === 0) {
            errors.exercises = 'Aggiungi almeno un esercizio';
        }
        
        // WEBAPP-GYM-003: Validazione dati numerici degli esercizi
        selectedExercises.forEach((ex, index) => {
            const exerciseErrors = [];
            
            // Valida Serie (deve essere almeno 1, eccetto cardio che può avere 1)
            const serie = parseInt(ex.serie);
            if (!serie || serie < 1) {
                exerciseErrors.push(`Serie non valide`);
            }
            
            // Valida Ripetizioni/Secondi/Minuti
            const reps = parseInt(ex.ripetizioni);
            if (!reps || reps < 0) {
                const label = ex.is_cardio === 1 ? 'Minuti' : (ex.is_isometric_scheda === 1 ? 'Secondi' : 'Ripetizioni');
                exerciseErrors.push(`${label} non validi`);
            }
            
            // Valida Peso (può essere 0 per esercizi a corpo libero)
            const peso = parseFloat(ex.peso);
            if (isNaN(peso) || peso < 0) {
                exerciseErrors.push(`Peso non valido`);
            }
            
            // Valida Tempo Recupero (deve essere almeno 0)
            const recupero = parseInt(ex.tempo_recupero);
            if (isNaN(recupero) || recupero < 0) {
                exerciseErrors.push(`Tempo recupero non valido`);
            }
            
            if (exerciseErrors.length > 0) {
                errors[`exercise_${index}`] = `Esercizio "${ex.nome}": ${exerciseErrors.join(', ')}`;
            }
        });
        
        setValidationErrors(errors);
        
        // Mostra messaggio di errore se ci sono problemi
        if (Object.keys(errors).length > 0) {
            const errorMessages = Object.values(errors).join('\n');
            setError(errorMessages);
            return false;
        }
        
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError(null);

        try {
            const api = getAuthenticatedApi();
            // Debug: controlla che tutti gli esercizi abbiano un ID
            const invalidExercises = selectedExercises.filter(ex => !ex.id);
            if (invalidExercises.length > 0) {
                console.error('Esercizi senza ID:', invalidExercises);
                setError('Errore: alcuni esercizi non hanno un ID valido');
                return;
            }

            const schedaData = {
                nome: formData.nome.trim(),
                descrizione: formData.descrizione.trim(),
                user_id: client.id,
                esercizi: selectedExercises.map(ex => {
                    // WEBAPP-GYM-003: Sanitizza e valida i dati numerici
                    const serie = parseInt(ex.serie) || (ex.is_cardio === 1 ? 1 : 3);
                    let ripetizioni = parseInt(ex.ripetizioni) || 12;
                    const peso = parseFloat(ex.peso) || 0;
                    const tempoRecupero = parseInt(ex.tempo_recupero) || 90;
                    
                    // Per esercizi cardio: converti minuti in secondi
                    if (ex.is_cardio === 1) {
                        // Se è cardio, ripetizioni contiene minuti, converti in secondi
                        ripetizioni = ripetizioni * 60;
                    }
                    
                    return {
                        esercizio_id: ex.id,
                        serie: Math.max(1, serie), // Almeno 1 serie
                        ripetizioni: Math.max(0, ripetizioni), // Almeno 0
                        peso: Math.max(0, peso), // Almeno 0
                        tempo_recupero: Math.max(0, tempoRecupero), // Almeno 0
                        note: ex.notes ? JSON.stringify(ex.notes) : JSON.stringify({ trainer: null, user: null, system: null }),
                        set_type: ex.set_type || 'normal',
                        linked_to_previous: ex.linked_to_previous || false,
                        is_isometric_scheda: ex.is_isometric_scheda || 0,
                        calorie: ex.calorie || null,
                        bpm: ex.bpm || null
                    };
                })
            };

            console.log('Dati scheda da inviare:', schedaData);

            await api.post('/schede.php', schedaData);
            onSuccess && onSuccess();
            onClose();

        } catch (err) {
            console.error('Errore nella creazione scheda:', err);
            setError(err.response?.data?.error || 'Errore nel salvataggio della scheda');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className={`${isFullscreen ? 'fixed inset-0 z-[9999] bg-[#faf9fc] dark:bg-gray-950' : 'w-full h-full'} flex flex-col overflow-hidden`}>
                {/* Header con titolo, cliente e pulsanti */}
                <div className="flex-shrink-0 bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-6 text-white dark:bg-gradient-to-r dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
                    {/* Riga superiore: Titolo con cliente + Pulsanti */}
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                            <Dumbbell className="h-6 w-6 text-white" />
                            Nuova scheda allenamento: <span className="font-normal">{client?.nome || client?.username || 'N/A'}</span>
                        </h2>
                        
                        <div className="flex items-center gap-2">
                            {/* WEBAPP-GYM-004: Bottone toggle fullscreen */}
                            <button
                                type="button"
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                title={isFullscreen ? 'Esci da schermo intero' : 'Modalità schermo intero'}
                            >
                                {isFullscreen ? (
                                    <Minimize2 className="h-5 w-5 text-white" />
                                ) : (
                                    <Maximize2 className="h-5 w-5 text-white" />
                                )}
                            </button>
                            
                            {/* Pulsante Salva */}
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={loading || Object.keys(validationErrors).length > 0}
                                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="animate-spin h-4 w-4" />
                                        <span>Salvataggio...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4" />
                                        <span>Salva scheda</span>
                                    </>
                                )}
                            </button>
                            
                            {/* Pulsante Annulla */}
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-white bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center gap-2"
                            >
                                <XCircle className="h-4 w-4" />
                                <span>Annulla</span>
                            </button>
                            
                            {/* Pulsante Chiudi */}
                            <button
                                type="button"
                                onClick={onClose}
                                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                                aria-label="Chiudi"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                    </div>
                    
                    {/* Form Info Scheda */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-white mb-1.5">
                                {t('gym.membersManager.workoutModal.workoutName')} <span className="text-red-200">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.nome}
                                onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                                className={`w-full px-3 py-2 rounded-lg border-2 focus:ring-2 focus:ring-white focus:border-white transition-colors bg-white bg-opacity-20 backdrop-blur-sm text-white placeholder-gray-300 text-sm ${
                                    validationErrors.nome ? 'border-red-200' : 'border-white border-opacity-30'
                                }`}
                                placeholder={t('gym.membersManager.workoutModal.workoutNamePlaceholder')}
                            />
                            {validationErrors.nome && (
                                <p className="mt-1 text-xs text-red-200 flex items-center">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {validationErrors.nome}
                                </p>
                            )}
                        </div>
                        
                        <div>
                            <label className="block text-xs font-medium text-white mb-1.5">
                                {t('gym.membersManager.workoutModal.description')}
                            </label>
                            <input
                                type="text"
                                value={formData.descrizione}
                                onChange={(e) => setFormData(prev => ({ ...prev, descrizione: e.target.value }))}
                                className="w-full px-3 py-2 rounded-lg border-2 border-white border-opacity-30 focus:ring-2 focus:ring-white focus:border-white transition-colors bg-white bg-opacity-20 backdrop-blur-sm text-white placeholder-gray-300 text-sm"
                                placeholder={t('gym.membersManager.workoutModal.descriptionPlaceholder')}
                            />
                        </div>
                    </div>
                </div>

                {/* Messaggi */}
                {(message || error) && (
                    <div className="flex-shrink-0 border-b border-[#dad8e5] bg-[#eeebf5] px-6 py-2 dark:border-white/10 dark:bg-gray-900">
                        {message && (
                            <div className={`p-2 rounded-md text-sm ${
                                messageType === 'success' ? 'bg-green-100 text-green-800' : 
                                messageType === 'error' ? 'bg-red-100 text-red-800' : 
                                'bg-blue-100 text-blue-800'
                            }`}>
                                {message}
                            </div>
                        )}
                        {error && (
                            <div className="p-2 bg-red-100 text-red-800 rounded-md text-sm">
                                {error}
                            </div>
                        )}
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="flex-shrink-0 border-b border-[#dad8e5] bg-white dark:border-white/10 dark:bg-gray-900">
                    <div className="flex">
                        <button
                            type="button"
                            onClick={() => setActiveTab('selection')}
                            className={`flex-1 px-6 py-2.5 text-sm font-medium transition-colors ${
                                activeTab === 'selection'
                                    ? 'border-b-2 border-emerald-500 bg-emerald-50 text-emerald-800 dark:border-indigo-500 dark:bg-gray-950 dark:text-gray-300'
                                    : 'text-[#3a3a3a]/75 hover:text-[#372d83] dark:text-white/70 dark:hover:text-white'
                            }`}
                        >
                            <div className="flex items-center justify-center space-x-2">
                                <Search className="h-4 w-4" />
                                <span>{t('gym.membersManager.workoutModal.exerciseSelection')}</span>
                                <span className="rounded-full border border-[#dad8e5] bg-white px-2 py-1 text-xs text-[#3a3a3a] dark:border-transparent dark:bg-gray-800 dark:text-white">
                                    {availableExercises.length}
                                </span>
                            </div>
                        </button>
                        
                        <button
                            type="button"
                            onClick={() => setActiveTab('selected')}
                            className={`flex-1 px-6 py-2.5 text-sm font-medium transition-colors ${
                                activeTab === 'selected'
                                    ? 'border-b-2 border-emerald-500 bg-emerald-50 text-emerald-800 dark:border-indigo-500 dark:bg-gray-950 dark:text-gray-300'
                                    : 'text-[#3a3a3a]/75 hover:text-[#372d83] dark:text-white/70 dark:hover:text-white'
                            }`}
                        >
                            <div className="flex items-center justify-center space-x-2">
                                <Target className="h-4 w-4" />
                                <span>{t('gym.membersManager.workoutModal.selectedExercises')}</span>
                                <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs text-emerald-800 dark:bg-gray-800 dark:text-gray-300">
                                    {selectedExercises.length}
                                </span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-hidden min-h-0">
                    {activeTab === 'selection' ? (
                        <div className="h-full min-h-0 overflow-hidden">
                            <NewExerciseSelector
                                searchTerm={searchTerm}
                                exercises={availableExercises}
                                selectedExercises={selectedExercises}
                                onSelectExercise={handleSelectExercise}
                                onShowAddExerciseForm={() => setShowAddExerciseForm(true)}
                                loading={loadingExercises || initialLoading}
                                hasMore={hasMore}
                                onLoadMore={loadMore}
                                onFiltersChange={handleFiltersChange}
                                onSearchChange={handleSearchChange}
                                gymExerciseSelector
                            />
                        </div>
                    ) : (
                        <div className="h-full min-h-0 overflow-y-auto bg-[#faf9fc] p-6 dark:bg-gray-900">
                            {validationErrors.exercises && (
                                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                        <AlertCircle className="h-4 w-4 mr-2" />
                                        {validationErrors.exercises}
                                    </p>
                                </div>
                            )}

                            {selectedExercises.length > 0 ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={selectedExercises.map(e => e.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {/* Griglia stile Instagram */}
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                            {selectedExercises.map((exercise, index) => {
                                                const isExpanded = expandedExercise === exercise.id;
                                                const previousExercise = index > 0 ? selectedExercises[index - 1] : null;
                                                
                                                // Determina l'URL dell'immagine
                                                let imageUrl = null;
                                                if (exercise.immagine_nome) {
                                                    imageUrl = `/api/serve_image.php?filename=${encodeURIComponent(exercise.immagine_nome)}`;
                                                } else if (exercise.immagine_statica) {
                                                    imageUrl = exercise.immagine_statica;
                                                }
                                                
                                                return (
                                                    <div key={exercise.id} className="relative">
                                                        {/* Visual indicator for linked exercises */}
                                                        {exercise.linked_to_previous && (
                                                            <div className="absolute -top-2 -left-2 z-10 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                                                <span className="text-white text-xs font-bold">+</span>
                                                            </div>
                                                        )}
                                                        
                                                        <div
                                                            className={`overflow-hidden rounded-lg border border-[#dad8e5] bg-white transition-shadow hover:shadow-lg dark:border-white/10 dark:bg-gray-900 ${
                                                                exercise.linked_to_previous ? 'border-t-2 border-blue-500' : ''
                                                            }`}
                                                        >
                                                            {/* Immagine grande stile Instagram */}
                                                            <div className="group relative aspect-square overflow-hidden bg-[#eeebf5] dark:bg-gray-800">
                                                                {imageUrl ? (
                                                                    <img
                                                                        src={imageUrl}
                                                                        alt={exercise.nome}
                                                                        className="w-full h-full object-cover select-none"
                                                                        onContextMenu={(e) => e.preventDefault()}
                                                                        draggable={false}
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            const placeholder = e.target.nextElementSibling;
                                                                            if (placeholder) placeholder.style.display = 'flex';
                                                                        }}
                                                                    />
                                                                ) : null}
                                                                <div className={`${imageUrl ? 'hidden' : 'flex'} w-full h-full items-center justify-center`}>
                                                                    <Image className="h-16 w-16 text-[#bca8d1] dark:text-white/40" />
                                                                </div>
                                                                
                                                                {/* Overlay con pulsanti al hover */}
                                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleToggleExpanded(exercise.id);
                                                                        }}
                                                                        className="rounded-full bg-white/90 p-2 text-[#3a3a3a] transition-colors hover:bg-white dark:bg-gray-800/95 dark:text-white dark:hover:bg-gray-700"
                                                                        title="Modifica esercizio"
                                                                    >
                                                                        <Edit className="h-4 w-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            removeExercise(exercise.id);
                                                                        }}
                                                                        className="rounded-full bg-white/90 p-2 text-red-600 transition-colors hover:bg-white dark:bg-gray-800/95 dark:text-red-300 dark:hover:bg-gray-700"
                                                                        title="Rimuovi esercizio"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </button>
                                                                </div>
                                                                
                                                                {/* Drag handle - visibile al hover */}
                                                                <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-move">
                                                                    <GripVertical className="h-5 w-5 text-white drop-shadow-lg" />
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Nome e info sotto l'immagine */}
                                                            <div className="p-3">
                                                                <h4 
                                                                    className="mb-2 cursor-pointer line-clamp-2 text-sm font-medium text-[#3a3a3a] hover:text-[#372d83] dark:text-white dark:hover:text-gray-300"
                                                                    onClick={() => handleToggleExpanded(exercise.id)}
                                                                    title={exercise.nome}
                                                                >
                                                                    {exercise.nome}
                                                                </h4>
                                                                
                                                                {/* Informazioni principali compatte */}
                                                                <div className="space-y-1">
                                                                    <div className="text-xs text-[#3a3a3a]/75 dark:text-white/70">
                                                                        {exercise.serie || 0} x {exercise.ripetizioni || 0}
                                                                        {exercise.is_cardio !== 1 && exercise.peso > 0 && ` @ ${exercise.peso}kg`}
                                                                    </div>
                                                                    
                                                                    <div className="flex flex-wrap items-center gap-1">
                                                                        {exercise.set_type && exercise.set_type !== 'normal' && (
                                                                            <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded">
                                                                                {exercise.set_type === 'superset' ? 'Superset' :
                                                                                 exercise.set_type === 'dropset' ? 'Dropset' :
                                                                                 exercise.set_type === 'circuit' ? 'Circuit' :
                                                                                 exercise.set_type === 'giant_set' ? 'Giant Set' :
                                                                                 exercise.set_type}
                                                                            </span>
                                                                        )}
                                                                        {exercise.is_cardio === 1 && (
                                                                            <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded">
                                                                                Cardio
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Modal per modificare i dettagli quando espanso */}
                                                        {isExpanded && (
                                                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" onClick={() => handleToggleExpanded(exercise.id)}>
                                                                <div 
                                                                    className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-[#dad8e5] bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <div className="border-b border-[#dad8e5] bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
                                                                        <div className="flex items-center justify-between gap-2">
                                                                            <h3 className="flex items-center gap-2 text-xl font-bold text-[#3a3a3a] dark:font-open-sans dark:text-white">
                                                                                <Dumbbell className="h-6 w-6 text-[#372d83] dark:text-gray-300" />
                                                                                {exercise.nome}
                                                                            </h3>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleToggleExpanded(exercise.id)}
                                                                                className="rounded-lg p-2 text-[#3a3a3a] transition-colors hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10"
                                                                            >
                                                                                <X className="h-6 w-6" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <div className="p-6 dark:bg-gray-900">
                                                                        
                                                                        {/* Form modifica esercizio - usa SortableExerciseItem espanso */}
                                                                        <SortableExerciseItem
                                                                            exercise={exercise}
                                                                            previousExercise={previousExercise}
                                                                            onUpdate={updateExerciseDetail}
                                                                            onRemove={removeExercise}
                                                                            onToggleDetails={handleToggleExpanded}
                                                                            isExpanded={true}
                                                                            isFirst={index === 0}
                                                                            t={t}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            ) : (
                                <div className="py-16 text-center">
                                    <Dumbbell size={64} className="mx-auto mb-4 text-[#bca8d1] dark:text-white/40" />
                                    <h3 className="mb-2 text-lg font-medium text-[#3a3a3a] dark:text-white">
                                        Nessun esercizio selezionato
                                    </h3>
                                    <p className="mb-6 text-[#3a3a3a]/75 dark:text-white/70">
                                        {t('gym.membersManager.workoutModal.goToSelectionTab')}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('selection')}
                                        className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-white transition-colors hover:bg-emerald-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                                    >
                                        <Search className="h-4 w-4 mr-2" />
                                        {t('gym.membersManager.workoutModal.selectExercises')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

            </div>

            {/* Modal Creazione Esercizio */}
            {showAddExerciseForm && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                    <div className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[#dad8e5] bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
                        <div className="flex shrink-0 items-center justify-between border-b border-[#dad8e5] bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
                            <h3 className="text-lg font-semibold text-[#3a3a3a] dark:font-open-sans dark:text-white">
                                Crea Nuovo Esercizio
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowAddExerciseForm(false)}
                                className="rounded-lg p-2 text-[#3a3a3a] transition-colors hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="max-h-[calc(85vh-80px)] overflow-y-auto bg-[#faf9fc] p-6 dark:bg-gray-900">
                            <ImprovedUserExerciseForm
                                embedded
                                onClose={() => setShowAddExerciseForm(false)}
                                onSuccess={handleExerciseCreated}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}